/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as HXKLineAndTagLayerSpec } from '../../../../layer/preset/hxKLine/hxKLineAndTag';
import { checkFieldType, createFieldValidator } from '../helper';
import * as utils from '../../../../util';
import { StockData } from '../../../../helper/hxKLine';

export type Spec = {
  layers: Partial<StandardChartLayerSpec>[];
};

type HXKLineAndTagLayerType = StandardChartLayerSpec &
  HXKLineAndTagLayerSpec & {
    __isEncodingValid?: boolean;
  };

/**
 * 检查标签日期字段类型
 * @param values 数据数组
 * @param field 字段名称
 * @param layerType 图层类型
 * @returns 是否有效
 */
function validateDateField(values: any[], field: string, layerType: string): boolean {
  if (!checkFieldType(values, field, 'date')) {
    utils.logger.warn(
      'warning',
      `The "${field}" field of the "${layerType}" layer must be a date type`
    );
    return false;
  }
  return true;
}

/**
 * 检查股票配置是否有效
 * @param stocks 股票配置数组
 * @param layerType 图层类型
 * @returns 是否有效
 */
function validateStocks(stocks: StockData[], layerType: string): boolean {
  let isValid = true;

  // 检查每个股票配置是否有效
  stocks.forEach((stock, index) => {
    // 检查股票代码
    if (!stock.code || typeof stock.code !== 'string') {
      utils.logger.warn(
        'warning',
        `Invalid stock code at index ${index} for "${layerType}" layer. Code must be a 6-digit string.`
      );
      isValid = false;
    }

    // 检查市场代码
    if (!stock.market || typeof stock.market !== 'string') {
      utils.logger.warn(
        'warning',
        `Invalid market code at index ${index} for "${layerType}" layer`
      );
      isValid = false;
    }
  });

  return isValid;
}

/**
 * 检查交易日期配置是否有效
 * @param tradeDate 交易日期配置
 * @param layerType 图层类型
 * @returns 是否有效
 */
function validateTradeDate(tradeDate: string[], layerType: string): boolean {
  if (
    !Array.isArray(tradeDate) ||
    tradeDate.length !== 2 ||
    !tradeDate.every(date => typeof date === 'string')
  ) {
    utils.logger.warn(
      'warning',
      `The "tradeDate" configuration for "${layerType}" layer must be an array of two date strings`
    );
    return false;
  }
  return true;
}

/**
 * 检查行情图叠加标签数据类型是否符合要求
 * @param layer 图层配置
 * @param data 数据
 * @returns 数据类型是否符合要求
 */
function validateDataTypes(layer: HXKLineAndTagLayerType, data: Data): boolean {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return false;
  }

  // 根据文档要求进行数据类型检测
  let isValid = true;

  // 检查from字段类型 - 必须是日期类型
  if (encoding.from && !validateDateField(values, encoding.from, layer.type)) {
    isValid = false;
  }

  // 检查to字段类型 - 必须是日期类型
  if (encoding.to && !validateDateField(values, encoding.to, layer.type)) {
    isValid = false;
  }

  // 检查stocks配置
  if (!layer.stocks || !Array.isArray(layer.stocks) || layer.stocks.length === 0) {
    utils.logger.warn(
      'warning',
      `The "stocks" configuration is required for "${layer.type}" layer`
    );
    isValid = false;
  } else if (!validateStocks(layer.stocks, layer.type)) {
    isValid = false;
  }

  // 检查tradeDate配置（如果提供）
  if (layer.tradeDate !== undefined && !validateTradeDate(layer.tradeDate, layer.type)) {
    isValid = false;
  }

  return isValid;
}

/**
 * 验证行情图叠加标签的编码配置
 * @param layer 图层配置
 * @returns 配置是否有效
 */
function validateEncoding(layer: HXKLineAndTagLayerType): boolean {
  return (
    createFieldValidator('from')(layer) &&
    createFieldValidator('to')(layer) &&
    createFieldValidator('concepts')(layer)
  );
}

/**
 * 行情图叠加标签数据处理
 * @param data 数据
 * @param spec 配置
 * @returns 是否有效
 */
export function hxKLineAndTag(data: Data, spec: Spec): boolean {
  let isValid = true;
  const { layers = [] } = spec;

  if (layers.every(layer => layer.type !== 'hxKLineAndTag')) {
    return isValid;
  }

  layers
    .filter((layer): layer is HXKLineAndTagLayerType => layer.type === 'hxKLineAndTag')
    .forEach(layer => {
      if (!validateEncoding(layer)) {
        isValid = false;
        layer.__isEncodingValid = false;
      }
      // 检查数据类型是否符合要求
      if (!validateDataTypes(layer, data)) {
        isValid = false;
        layer.__isEncodingValid = false;
        utils.logger.warn('warning', `Data type check failed for "${layer.type}" layer`);
      }
    });

  return isValid;
}
